import React, { useState } from "react";
import axios from "axios";
import { BRAND } from "../lib/siteData";
import { Phone, Mail, MessageCircle, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Residential Build",
  "Commercial Development",
  "Infrastructure",
  "Renovation",
  "Architectural Design",
  "Consultation Only",
];

const BUDGET_RANGES = [
  "Under USD 100K",
  "USD 100K – 500K",
  "USD 500K – 2M",
  "USD 2M – 10M",
  "Over USD 10M",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    budget_range: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and your message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our team will be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", project_type: "", budget_range: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to submit. Please try again shortly.";
      toast.error(typeof detail === "string" ? detail : "Unable to submit. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="relative py-20 lg:py-28 bg-[#F8FAFC]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left info */}
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#123E73]" />
              <span className="text-xs tracking-[0.3em] font-semibold text-[#123E73]">CONTACT</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#0A192F]">
              Let&apos;s design your next project.
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-lg">
              Share a few details and our engineering team will respond within one business day.
              Prefer a direct line? Call, email or message us on WhatsApp.
            </p>

            <div className="mt-10 space-y-5" data-testid="contact-details">
              <a href={`tel:${BRAND.phone}`} className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 text-[#123E73] group-hover:bg-[#F5A400] group-hover:text-[#0A192F] group-hover:border-[#F5A400] transition-all">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.25em] font-semibold text-slate-500">PHONE</div>
                  <div className="text-[#0A192F] font-bold">{BRAND.phone}</div>
                </div>
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 group" data-testid="contact-email">
                <div className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 text-[#123E73] group-hover:bg-[#F5A400] group-hover:text-[#0A192F] group-hover:border-[#F5A400] transition-all">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.25em] font-semibold text-slate-500">EMAIL</div>
                  <div className="text-[#0A192F] font-bold">{BRAND.email}</div>
                </div>
              </a>
              <a
                href={`https://wa.me/${BRAND.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-whatsapp"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 text-[#123E73] group-hover:bg-[#F5A400] group-hover:text-[#0A192F] group-hover:border-[#F5A400] transition-all">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.25em] font-semibold text-slate-500">WHATSAPP</div>
                  <div className="text-[#0A192F] font-bold">{BRAND.whatsappDisplay}</div>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-office">
                <div className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 text-[#123E73]">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.25em] font-semibold text-slate-500">OFFICE</div>
                  <div className="text-[#0A192F] font-bold">{BRAND.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-hours">
                <div className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 text-[#123E73]">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.25em] font-semibold text-slate-500">HOURS</div>
                  <div className="text-[#0A192F] font-bold">{BRAND.hours}</div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-10 aspect-[16/10] overflow-hidden border border-slate-200 bg-slate-100" data-testid="contact-map">
              <iframe
                title="LNZ Office Location"
                src="https://www.google.com/maps?q=4.5%20Camp%20Road%2C%20Kingston%2C%20Jamaica&output=embed"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal className="lg:col-span-7" delay={120}>
            <form
              onSubmit={onSubmit}
              className="bg-white border border-slate-200 p-7 lg:p-10 shadow-sm"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *" testId="contact-field-name">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="w-full border-0 border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors"
                    placeholder="Jane Bennett"
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Email Address *" testId="contact-field-email">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="w-full border-0 border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors"
                    placeholder="jane@domain.com"
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="Phone" testId="contact-field-phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full border-0 border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors"
                    placeholder="+1 (876) 000-0000"
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Project Type" testId="contact-field-project-type">
                  <select
                    value={form.project_type}
                    onChange={update("project_type")}
                    className="w-full border-0 border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors"
                    data-testid="contact-input-project-type"
                  >
                    <option value="">Select a category</option>
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Budget Range" testId="contact-field-budget" className="sm:col-span-2">
                  <select
                    value={form.budget_range}
                    onChange={update("budget_range")}
                    className="w-full border-0 border-b border-slate-300 bg-transparent py-3 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors"
                    data-testid="contact-input-budget"
                  >
                    <option value="">Select a range</option>
                    {BUDGET_RANGES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Tell us about your project *" testId="contact-field-message" className="sm:col-span-2">
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full border border-slate-200 bg-slate-50 p-4 text-[#0A192F] focus:outline-none focus:border-[#123E73] transition-colors resize-none"
                    placeholder="Location, scope, timeline and any details that help us prepare..."
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white hover:bg-[#F5A400] hover:text-[#0A192F] font-bold px-10 py-4 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry <Send className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-slate-500">
                We respect your privacy. Your details are used only to respond to your inquiry.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testId, className = "" }) {
  return (
    <label className={`flex flex-col ${className}`} data-testid={testId}>
      <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
